/**
 * The dialects settings can select, and how they are named on the chat API.
 *
 * ace-common stores a dialect as an iso code ("ar-LB"); the chat service names it instead
 * ("Lebanese") and answers 422 for a dialect it does not know, so a code with no entry here
 * must be omitted from the request rather than passed through.
 */
export interface DialectDefinition {
  /** ace-common iso code — what settings stores in `avatarLanguage`. */
  iso: string;
  /** The value the chat service accepts as its `dialect` request field. */
  chatValue: string;
  /** Dropdown labels. */
  en: string;
  ar: string;
}

/**
 * Arabic's four dialects, in the order the settings dropdown lists them. Every other language
 * offers a single "Default" entry instead, which stores the plain base iso and so has no dialect.
 */
export const ARABIC_DIALECTS: DialectDefinition[] = [
  { iso: "ar-LB", chatValue: "Lebanese", en: "Lebanese", ar: "لبناني" },
  { iso: "ar-EG", chatValue: "Egyptian", en: "Egyptian", ar: "مصري" },
  { iso: "ar-SA", chatValue: "Saudi", en: "Saudi", ar: "سعودي" },
  { iso: "ar-BH", chatValue: "Bahraini", en: "Bahraini", ar: "بحريني" },
];

/**
 * The `dialect` for a chat request, from the language iso settings has stored.
 *
 * Returns null whenever the selection carries no dialect the chat service knows — a plain base
 * iso ("ar", "en"), a non-Arabic language, or an Arabic region we have no entry for ("ar-AE").
 * Null means "no dialect override": the profile's own prompts are used, which is what an
 * unmapped selection should do. Sending the raw code instead would fail the whole turn with 422.
 */
export const resolveChatDialect = (iso: string | null | undefined): string | null => {
  const normalized = (iso ?? "").trim().toLowerCase();
  if (!normalized) return null;

  const match = ARABIC_DIALECTS.find(
    (dialect) =>
      dialect.iso.toLowerCase() === normalized ||
      // Tolerated so a stored name ("Bahraini") resolves too: ace-common's dialect list is the
      // usual source of the stored value, but it is not guaranteed to be iso-shaped.
      dialect.chatValue.toLowerCase() === normalized,
  );

  return match ? match.chatValue : null;
};
